import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import type { SpatialTreeItem } from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { formatValue, getCategory } from '@viewer/lib/ifcItemData'
import {
  aplicarContencao,
  contentoresDaArvore,
  lerReferencias,
  type Referenciado,
} from './contencao'

// State carried by the inline right-click menu. Held at SpatialTree
// root so only one menu exists at a time; rows fire `onOpenMenu(...)`.
interface TreeMenuState {
  x: number
  y: number
  node: DisplayNode
}

interface NodeMeta {
  category: string
  name: string
  tag: string
}

const SPATIAL_CATEGORIES = new Set([
  'IFCPROJECT',
  'IFCSITE',
  'IFCBUILDING',
  'IFCBUILDINGSTOREY',
  'IFCSPACE',
])

/** Contentores a quem se pergunta o que referenciam
 *  (IfcRelReferencedInSpatialStructure). */
const CONTENTOR_COM_REFERENCIAS =
  /^IFC(PROJECT|SITE|BUILDING|BUILDINGSTOREY|SPACE|SPATIALZONE|EXTERNALSPATIALELEMENT|FACILITY|FACILITYPART|BRIDGE|BRIDGEPART|ROAD|ROADPART|RAILWAY|RAILWAYPART|MARINEFACILITY|MARINEPART)$/

function isSpatial(cat: string | null | undefined): boolean {
  if (!cat) return false
  return SPATIAL_CATEGORIES.has(cat.toUpperCase())
}

// Display tree built on top of the raw spatial tree returned by
// FragmentsModel.getSpatialStructure. We separate spatial children
// from leaf elements at every level: spatial containers stay nested,
// while leaf elements get folded under a synthetic "ELEMENTOS (n)"
// wrapper grouped by IFC category. This matches the BCF viewer's
// hierarchy convention.

type DisplayNode =
  | {
      kind: 'real'
      key: string
      raw: SpatialTreeItem
      meta: NodeMeta | undefined
      isContainer: boolean
      children: DisplayNode[]
      /** Elemento que o contentor acima só referencia (não contém). */
      referenciado?: boolean
    }
  | {
      kind: 'wrapper'
      key: string
      label: string
      count: number
      children: DisplayNode[]
      /** A lista REFERENCIADOS de um contentor: não conta para esconder
       *  ou seleccionar o contentor (os elementos são de outro). */
      referencias?: boolean
    }
  | {
      kind: 'group'
      key: string
      label: string
      count: number
      children: DisplayNode[]
    }

function categoryOfReal(d: DisplayNode): string {
  if (d.kind !== 'real') return ''
  return (d.raw.category ?? d.meta?.category ?? '').toUpperCase()
}

// Walk a DisplayNode subtree and collect every real leaf localId
// (non-container reals + the localId of containers themselves so the
// container can hide its own geometry too if it has any).
function collectRealIds(node: DisplayNode, out: number[]) {
  if (node.kind === 'real' && node.raw.localId != null) {
    out.push(node.raw.localId)
  }
  for (const c of node.children) {
    // O que o contentor só referencia pertence a outro contentor.
    if (c.kind === 'wrapper' && c.referencias) continue
    collectRealIds(c, out)
  }
}

function buildDisplay(
  raw: SpatialTreeItem,
  getMeta: (id: number) => NodeMeta | undefined,
  parentKey = '',
  referenciados: Map<number, Referenciado[]> = new Map(),
): DisplayNode {
  const meta = raw.localId != null ? getMeta(raw.localId) : undefined
  const cat = (raw.category ?? meta?.category ?? '').toUpperCase()
  const isContainer = isSpatial(cat)
  const baseKey = `${parentKey}/${cat}-${raw.localId ?? 'x'}`

  // O que este contentor referencia, à parte e marcado.
  const refs = raw.localId != null ? referenciados.get(raw.localId) ?? [] : []
  const referencias: DisplayNode[] =
    refs.length === 0
      ? []
      : [
          {
            kind: 'wrapper',
            key: `${baseKey}/refs`,
            label: 'REFERENCIADOS',
            count: refs.length,
            referencias: true,
            children: refs.map((r) => ({
              kind: 'real' as const,
              key: `${baseKey}/ref-${r.localId}`,
              raw: { localId: r.localId, category: r.category, children: [] },
              meta: getMeta(r.localId),
              isContainer: false,
              children: [],
              referenciado: true,
            })),
          },
        ]

  const childRaws = raw.children ?? []
  if (childRaws.length === 0) {
    return {
      kind: 'real',
      key: baseKey,
      raw,
      meta,
      isContainer,
      children: referencias,
    }
  }

  const childDisplays = childRaws.map((c, i) =>
    buildDisplay(c, getMeta, `${baseKey}/${i}`, referenciados),
  )

  // Split into spatial containers and leaf elements.
  const containers: DisplayNode[] = []
  const elements: DisplayNode[] = []
  for (const c of childDisplays) {
    if (c.kind === 'real' && isSpatial(categoryOfReal(c))) {
      containers.push(c)
    } else {
      elements.push(c)
    }
  }

  let outChildren: DisplayNode[]
  if (elements.length === 0) {
    outChildren = containers
  } else {
    // Group leaf elements by category.
    const byCat = new Map<string, DisplayNode[]>()
    for (const e of elements) {
      const eCat =
        e.kind === 'real'
          ? categoryOfReal(e) || 'DESCONHECIDO'
          : 'DESCONHECIDO'
      const list = byCat.get(eCat) ?? []
      list.push(e)
      byCat.set(eCat, list)
    }
    const groupers: DisplayNode[] = Array.from(byCat.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([groupCat, items]) => ({
        kind: 'group' as const,
        key: `${baseKey}/grp-${groupCat}`,
        label: groupCat,
        count: items.length,
        children: items,
      }))

    if (cat === 'IFCBUILDINGSTOREY' || cat === 'IFCSPACE') {
      outChildren = [
        ...containers,
        {
          kind: 'wrapper',
          key: `${baseKey}/wrap`,
          label: 'ELEMENTOS',
          count: elements.length,
          children: groupers,
        },
      ]
    } else {
      // For non-storey containers (e.g. an assembly), inline the
      // groupers without the ELEMENTOS wrapper.
      outChildren = [...containers, ...groupers]
    }
  }

  return {
    kind: 'real',
    key: baseKey,
    raw,
    meta,
    isContainer,
    children: [...outChildren, ...referencias],
  }
}

/** Um só mapa vazio, partilhado: devolver um `new Map()` a cada render
 *  fazia tudo o que dele depende correr sempre. */
const EMPTY_META: Map<number, NodeMeta> = new Map()
const EMPTY_HIDDEN: Set<number> = new Set()
const EMPTY_REFS: Map<number, Referenciado[]> = new Map()

function SpatialTree() {
  const activeModelId = useAppStore((s) => s.activeModelId)
  const selection = useAppStore((s) => s.selection)
  const hiddenCount = useAppStore((s) => s.hiddenCount)
  const [arvore, setArvore] = useState<{
    modelId: string | null
    root: SpatialTreeItem | null
    /** Por contentor: o que ele só referencia (ver contencao.ts). */
    referenciados: Map<number, Referenciado[]>
    error: string | null
    loading: boolean
  }>({ modelId: null, root: null, referenciados: new Map(), error: null, loading: false })
  // O meta guarda consigo o modelo de que veio, e assim fica velho
  // sozinho quando o modelo muda. Antes era limpo no corpo de um
  // efeito, que encadeia renders (era o que o lint apontava) e deixava
  // um render a mostrar o meta do modelo anterior sobre a árvore nova.
  const [metaCarga, setMetaCarga] = useState<{
    modelId: string | null
    meta: Map<number, NodeMeta>
  }>({ modelId: null, meta: new Map() })
  // Set of localIds currently invisible in the active model. Refreshed
  // whenever hiddenCount changes (the viewerApi hide/isolate/showAll
  // imperatives bump it via _refreshVisibilityState).
  const [escondidos, setEscondidos] = useState<{
    modelId: string | null
    ids: Set<number>
  }>({ modelId: null, ids: new Set() })
  const [menu, setMenu] = useState<TreeMenuState | null>(null)
  const pendingRef = useRef<Set<number>>(new Set())
  const inflightRef = useRef(false)

  const meta =
    metaCarga.modelId === activeModelId ? metaCarga.meta : EMPTY_META
  const daArvore = arvore.modelId === activeModelId
  const root = daArvore ? arvore.root : null
  const referenciados = daArvore ? arvore.referenciados : EMPTY_REFS
  const error = daArvore ? arvore.error : null
  const loading = daArvore ? arvore.loading : Boolean(activeModelId)
  const hiddenIds =
    escondidos.modelId === activeModelId ? escondidos.ids : EMPTY_HIDDEN

  // Refresh the hidden-ids snapshot for the active model on any
  // visibility change. We requery the model directly because the slice
  // only carries a count; the spatial tree needs the atual ids.
  useEffect(() => {
    if (!activeModelId) return
    let cancelled = false
    void viewerApi
      .getHiddenItemsMap()
      .then((map) => {
        if (cancelled) return
        setEscondidos({
          modelId: activeModelId,
          ids: map.get(activeModelId) ?? new Set(),
        })
      })
      .catch(() => {
        if (!cancelled) setEscondidos({ modelId: activeModelId, ids: new Set() })
      })
    return () => {
      cancelled = true
    }
  }, [activeModelId, hiddenCount])

  useEffect(() => {
    if (!activeModelId) return
    const m = viewerApi.getModel(activeModelId)
    if (!m) {
      const unsubscribe = viewerApi.subscribe(() => {
        const ready = viewerApi.getModel(activeModelId)
        if (ready) {
          unsubscribe()
          loadTree(ready, activeModelId)
        }
      })
      return unsubscribe
    }
    return loadTree(m, activeModelId)

    function loadTree(
      mm: ReturnType<typeof viewerApi.getModel> & object,
      modelId: string,
    ): () => void {
      let cancelled = false
      mm.getSpatialStructure()
        .then(async (tree) => {
          if (cancelled || activeModelId !== modelId) return
          // A contenção manda: o motor pode pôr um elemento debaixo de
          // quem só o referencia. Se a leitura das referências falhar,
          // fica a árvore do motor.
          let arvoreFinal = tree
          let refs: Map<number, Referenciado[]> = new Map()
          try {
            const lidas = await lerReferencias(
              mm as unknown as Parameters<typeof lerReferencias>[0],
              contentoresDaArvore(tree, (c) => CONTENTOR_COM_REFERENCIAS.test(c)),
            )
            const r = aplicarContencao(tree, lidas.referenciasPorContentor, lidas.contentorDe)
            arvoreFinal = r.raiz
            refs = r.referenciados
          } catch {
            /* fica a árvore do motor */
          }
          if (cancelled || activeModelId !== modelId) return
          setArvore({
            modelId,
            root: pruneTree(arvoreFinal),
            referenciados: refs,
            error: null,
            loading: false,
          })
        })
        .catch((err: unknown) => {
          if (cancelled) return
          setArvore({
            modelId,
            root: null,
            referenciados: new Map(),
            error: err instanceof Error ? err.message : String(err),
            loading: false,
          })
        })
      return () => {
        cancelled = true
      }
    }
  }, [activeModelId])

  const flush = useCallback(async () => {
    if (inflightRef.current) return
    if (!activeModelId) return
    const model = viewerApi.getModel(activeModelId)
    if (!model) return
    inflightRef.current = true
    try {
      while (pendingRef.current.size > 0) {
        const batch: number[] = []
        for (const id of pendingRef.current) {
          batch.push(id)
          if (batch.length >= 500) break
        }
        for (const id of batch) pendingRef.current.delete(id)
        const items = await model.getItemsData(batch, {
          attributesDefault: true,
          relationsDefault: { attributes: false, relations: false },
        })
        setMetaCarga((antes) => {
          // Só se acumula sobre o meta do MESMO modelo: um lote que
          // chegue atrasado depois de trocar de modelo não pode
          // contaminar a árvore nova.
          const next =
            antes.modelId === activeModelId ? new Map(antes.meta) : new Map()
          items.forEach((item, idx) => {
            if (!item || typeof item !== 'object') return
            const o = item as Record<string, unknown>
            next.set(batch[idx], {
              category: getCategory(o),
              name: formatValue(o.Name),
              tag: formatValue(o.Tag),
            })
          })
          return { modelId: activeModelId, meta: next }
        })
      }
    } finally {
      inflightRef.current = false
    }
  }, [activeModelId])

  const requestMeta = useCallback(
    (ids: number[]) => {
      let added = false
      for (const id of ids) {
        if (id == null) continue
        if (meta.has(id)) continue
        if (!pendingRef.current.has(id)) {
          pendingRef.current.add(id)
          added = true
        }
      }
      if (added) void flush()
    },
    [meta, flush],
  )

  const display = useMemo(() => {
    if (!root) return null
    return buildDisplay(root, (id) => meta.get(id), '', referenciados)
  }, [root, meta, referenciados])

  if (!activeModelId) {
    return (
      <div className="px-3 py-2 text-xs italic text-[var(--color-text-dim)]">
        Carrega um modelo para ver a hierarquia.
      </div>
    )
  }
  if (loading && !root) {
    return (
      <div className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
        A construir hierarquia…
      </div>
    )
  }
  if (error) {
    return (
      <div className="m-2 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-2 text-xs text-[var(--color-error-text)]">
        {error}
      </div>
    )
  }
  if (!display) return null

  return (
    <div className="relative overflow-auto px-1 py-1 text-xs">
      <DisplayNodeView
        node={display}
        depth={0}
        modelId={activeModelId}
        selectedLocalId={selection?.localId ?? null}
        hiddenIds={hiddenIds}
        requestMeta={requestMeta}
        onOpenMenu={setMenu}
      />
      {menu && (
        <TreeContextMenu
          state={menu}
          modelId={activeModelId}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  )
}

function pruneTree(node: SpatialTreeItem): SpatialTreeItem {
  if (!node.children || node.children.length === 0) return node
  const cleaned: SpatialTreeItem[] = []
  for (const raw of node.children) {
    const child = pruneTree(raw)
    const sameId =
      child.localId !== null &&
      node.localId !== null &&
      child.localId === node.localId
    // Any synthetic node (no localId of its own) gets hoisted: with
    // localId === null we have no IFC entity, so the row would only
    // show an "IFCXXX #?" placeholder above the real named entity.
    // Better to flatten the wrapper away.
    const isSynthetic = child.localId === null || child.localId === undefined
    if (sameId) {
      if (child.children) cleaned.push(...child.children)
      continue
    }
    if (isSynthetic) {
      if (child.children && child.children.length > 0) {
        cleaned.push(...child.children)
      }
      continue
    }
    cleaned.push(child)
  }
  return { ...node, children: cleaned }
}

interface NodeProps {
  node: DisplayNode
  depth: number
  modelId: string
  selectedLocalId: number | null
  hiddenIds: Set<number>
  requestMeta: (ids: number[]) => void
  onOpenMenu: (state: TreeMenuState) => void
}

// Visibility status of a node: 'visible' (fully shown), 'hidden' (every
// real descendant + self is hidden) or 'partial' (mixed). Used both for
// styling and to decide what the eye-button toggle should do (visible
// → hide, hidden/partial → show).
type VisibilityStatus = 'visible' | 'hidden' | 'partial'

function visibilityForNode(
  node: DisplayNode,
  hiddenIds: Set<number>,
): { status: VisibilityStatus; ids: number[] } {
  const ids: number[] = []
  collectRealIds(node, ids)
  if (ids.length === 0) return { status: 'visible', ids }
  let hidden = 0
  for (const id of ids) if (hiddenIds.has(id)) hidden += 1
  if (hidden === 0) return { status: 'visible', ids }
  if (hidden === ids.length) return { status: 'hidden', ids }
  return { status: 'partial', ids }
}

function DisplayNodeView({
  node,
  depth,
  modelId,
  selectedLocalId,
  hiddenIds,
  requestMeta,
  onOpenMenu,
}: NodeProps) {
  const applyBulkSelection = useAppStore((s) => s.applyBulkSelection)
  const hasChildren = node.children.length > 0

  // Default-open logic: spatial containers + the ELEMENTOS wrapper.
  const defaultOpen =
    (node.kind === 'real' && node.isContainer && depth < 4) ||
    node.kind === 'wrapper'
  const [open, setOpen] = useState(defaultOpen)

  // Always request metadata for self when this node is real.
  useEffect(() => {
    if (node.kind === 'real' && node.raw.localId != null) {
      requestMeta([node.raw.localId])
    }
  }, [node, requestMeta])

  // When opening, eagerly fetch metadata for ALL real-element ids in
  // the subtree so the per-category groupers can sort by IFC class
  // immediately.
  useEffect(() => {
    if (!open) return
    const ids: number[] = []
    const walk = (d: DisplayNode) => {
      if (d.kind === 'real' && d.raw.localId != null) ids.push(d.raw.localId)
      d.children.forEach(walk)
    }
    node.children.forEach(walk)
    if (ids.length > 0) requestMeta(ids)
  }, [open, node, requestMeta])

  const isSelected =
    node.kind === 'real' &&
    selectedLocalId !== null &&
    node.raw.localId === selectedLocalId

  const visibility = useMemo(
    () => visibilityForNode(node, hiddenIds),
    [node, hiddenIds],
  )

  // Click on row label:
  //  • Leaf real node → selectByLocalId (single highlight in canvas)
  //  • Container (project/site/building/storey/space) → bulk select
  //    every real descendant so the user can hide/isolate/etc the
  //    whole group from the ribbon. Ctrl/Shift adds to the current
  //    selection instead of replacing.
  //  • Wrapper / group → bulk select the elements inside the group.
  const onSelect = (e: React.MouseEvent) => {
    if (node.kind === 'real' && !node.isContainer) {
      if (node.raw.localId == null) return
      void viewerApi.selectByLocalId(modelId, node.raw.localId)
      return
    }
    const ids: number[] = []
    collectRealIds(node, ids)
    if (ids.length === 0) return
    const targets = ids.map((localId) => ({ modelId, localId }))
    const additive = e.ctrlKey || e.metaKey || e.shiftKey
    applyBulkSelection(targets, additive ? 'add' : 'replace')
  }

  const onToggleHidden = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (visibility.ids.length === 0) return
    const nextVisible = visibility.status !== 'visible'
    void viewerApi.setItemsVisibility(modelId, visibility.ids, nextVisible)
  }

  const onContextMenu = (e: React.MouseEvent) => {
    // Right-click on wrapper/group/container opens an inline menu with
    // the alternate "select only this container" plus visibility
    // shortcuts. Leaves don't need it: single click is unambiguous.
    if (node.kind === 'real' && !node.isContainer) return
    e.preventDefault()
    e.stopPropagation()
    onOpenMenu({ x: e.clientX, y: e.clientY, node })
  }

  const isDimmed = visibility.status === 'hidden'
  const isPartial = visibility.status === 'partial'

  return (
    <div>
      <div
        onContextMenu={onContextMenu}
        className={`group flex items-center gap-1 rounded px-1 py-0.5 ${
          isSelected
            ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
            : 'hover:bg-[var(--color-panel-elevated)] text-[var(--color-text)]'
        } ${isDimmed ? 'opacity-50' : ''}`}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <button
          type="button"
          onClick={() => hasChildren && setOpen((v) => !v)}
          className="w-4 shrink-0 text-center text-[10px] text-[var(--color-text-muted)]"
          aria-label={open ? 'Fechar' : 'Abrir'}
        >
          {hasChildren ? (open ? '▾' : '▸') : ''}
        </button>
        <NodeLabel node={node} onSelect={onSelect} hidden={isDimmed} />
        {visibility.ids.length > 0 && (
          <button
            type="button"
            onClick={onToggleHidden}
            title={
              visibility.status === 'visible'
                ? `Esconder (${visibility.ids.length})`
                : visibility.status === 'hidden'
                  ? `Mostrar (${visibility.ids.length})`
                  : `Mostrar (${visibility.ids.length} elementos, alguns escondidos)`
            }
            aria-label={
              visibility.status === 'visible' ? 'Esconder' : 'Mostrar'
            }
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-[var(--color-bg)] hover:text-[var(--color-text)] ${
              visibility.status === 'visible'
                ? 'text-[var(--color-text-dim)]'
                : isPartial
                  ? 'text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)]'
            }`}
          >
            {visibility.status === 'visible' ? (
              <Eye size={12} strokeWidth={1.6} />
            ) : (
              <EyeOff size={12} strokeWidth={1.6} />
            )}
          </button>
        )}
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map((child) => (
            <DisplayNodeView
              key={child.key}
              node={child}
              depth={depth + 1}
              modelId={modelId}
              selectedLocalId={selectedLocalId}
              hiddenIds={hiddenIds}
              requestMeta={requestMeta}
              onOpenMenu={onOpenMenu}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function NodeLabel({
  node,
  onSelect,
  hidden,
}: {
  node: DisplayNode
  onSelect: (e: React.MouseEvent) => void
  hidden: boolean
}) {
  if (node.kind === 'wrapper' || node.kind === 'group') {
    return (
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 truncate cursor-pointer text-left"
        title={`Selecionar todos os ${node.count} elementos (Ctrl/Shift para adicionar)`}
      >
        <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          {node.label}
        </span>
        <span className="ml-1 text-[10px] text-[var(--color-text-dim)]">
          ({node.count})
        </span>
      </button>
    )
  }

  // Real node.
  const cat = (node.raw.category ?? node.meta?.category ?? '').toUpperCase()
  const name =
    node.meta?.name && node.meta.name !== '-' ? node.meta.name : ''
  const tag = node.meta?.tag && node.meta.tag !== '-' ? node.meta.tag : ''
  const localId = node.raw.localId
  const strike = hidden ? 'line-through decoration-[var(--color-text-dim)]/60' : ''

  // Containers (Project/Site/Building/Storey/Space): show "IFCXXX <Name> #localId".
  // Click selects every real descendant so the user can hide/isolate
  // the whole branch from the ribbon, much faster than expanding and
  // multi-clicking.
  if (node.isContainer) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={`flex min-w-0 flex-1 items-baseline gap-1 truncate cursor-pointer text-left ${strike}`}
        title={`Selecionar tudo dentro de ${cat || 'container'}${
          name ? ' · ' + name : ''
        }${localId != null ? ' · #' + localId : ''} (Ctrl/Shift para adicionar)`}
      >
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          {cat || '?'}
        </span>
        {name && (
          <span className="truncate text-[var(--color-text)]">{name}</span>
        )}
        {localId != null && (
          <span className="ml-auto shrink-0 font-mono text-[10px] text-[var(--color-text-dim)]">
            #{localId}
          </span>
        )}
      </button>
    )
  }

  // Leaf element: Name + #localId reference (muted, right-aligned).
  // The IFC class is already on the grouper above so we don't repeat
  // it here, but the localId stays so the user can cross-reference
  // with the properties panel and BCF viewpoints.
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={localId == null}
      className={`flex min-w-0 flex-1 items-baseline gap-1 truncate text-left ${strike} ${
        localId == null ? 'cursor-default' : 'cursor-pointer'
      }`}
      title={`${cat || '?'}${name ? ' · ' + name : ''}${
        tag ? ' · Tag ' + tag : ''
      }${localId != null ? ' · #' + localId : ''}${
        node.referenciado
          ? ' · referenciado aqui (IfcRelReferencedInSpatialStructure), não contido'
          : ''
      }`}
    >
      {node.referenciado && (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          {cat || '?'}
        </span>
      )}
      {name ? (
        <span className="truncate text-[var(--color-text)]">{name}</span>
      ) : node.meta ? (
        <span className="truncate text-[var(--color-text-muted)]">(sem nome)</span>
      ) : (
        <span className="text-[10px] italic text-[var(--color-text-muted)]">
          …
        </span>
      )}
      {node.referenciado && (
        <span className="shrink-0 text-[10px] italic text-[var(--color-text-dim)]">(referenciado)</span>
      )}
      {localId != null && (
        <span className="ml-auto shrink-0 font-mono text-[10px] text-[var(--color-text-dim)]">
          #{localId}
        </span>
      )}
    </button>
  )
}

// Right-click menu rendered at viewport coords. Closes on outside
// click / Esc. The menu items are picked based on the kind of node:
//  • real container (has localId of its own + descendants): "select
//    only this", "select contents", and visibility shortcuts.
//  • wrapper / group (no own localId): contents-only operations.
function TreeContextMenu({
  state,
  modelId,
  onClose,
}: {
  state: TreeMenuState
  modelId: string
  onClose: () => void
}) {
  const applyBulkSelection = useAppStore((s) => s.applyBulkSelection)
  const setSelection = useAppStore((s) => s.setSelection)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    const onPointerDown = (e: PointerEvent) => {
      const node = ref.current
      if (!node) return
      if (node.contains(e.target as Node)) return
      onClose()
    }
    window.addEventListener('keydown', onKey, { capture: true })
    window.addEventListener('pointerdown', onPointerDown, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true })
      window.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      })
    }
  }, [onClose])

  const node = state.node

  // Keep the menu inside the viewport.
  const W = 240
  const x = Math.min(state.x, window.innerWidth - W - 8)
  const y = Math.min(state.y, window.innerHeight - 240)

  const containerOwnId =
    node.kind === 'real' && node.isContainer && node.raw.localId != null
      ? node.raw.localId
      : null
  const contentIds = useMemo(() => {
    const ids: number[] = []
    // For "contents only" we want descendants but NOT the container's
    // own localId. For wrappers/groups, every real id is a descendant.
    if (node.kind === 'real') {
      for (const c of node.children) collectRealIds(c, ids)
    } else {
      collectRealIds(node, ids)
    }
    return ids
  }, [node])

  const titleLabel =
    node.kind === 'real'
      ? (node.raw.category ?? node.meta?.category ?? '').toUpperCase() +
        (node.meta?.name ? ` ${node.meta.name}` : '')
      : node.label

  const run = (fn: () => void | Promise<void>) => {
    void Promise.resolve(fn()).finally(onClose)
  }

  const items: Array<
    | {
        type: 'item'
        label: string
        onClick: () => void
        disabled?: boolean
      }
    | { type: 'sep' }
  > = []

  if (containerOwnId !== null) {
    items.push({
      type: 'item',
      label: 'Selecionar só este container',
      onClick: () =>
        run(() => {
          setSelection({ modelId, localId: containerOwnId })
        }),
    })
  }
  items.push({
    type: 'item',
    label: `Selecionar conteúdos (${contentIds.length})`,
    disabled: contentIds.length === 0,
    onClick: () =>
      run(() => {
        const targets = contentIds.map((localId) => ({ modelId, localId }))
        applyBulkSelection(targets, 'replace')
      }),
  })
  if (containerOwnId !== null && contentIds.length > 0) {
    items.push({
      type: 'item',
      label: `Selecionar container + conteúdos (${contentIds.length + 1})`,
      onClick: () =>
        run(() => {
          const targets = [containerOwnId, ...contentIds].map((localId) => ({
            modelId,
            localId,
          }))
          applyBulkSelection(targets, 'replace')
        }),
    })
  }
  items.push({ type: 'sep' })
  if (containerOwnId !== null) {
    items.push({
      type: 'item',
      label: 'Esconder só o container',
      onClick: () =>
        run(() =>
          viewerApi.setItemsVisibility(modelId, [containerOwnId], false),
        ),
    })
  }
  items.push({
    type: 'item',
    label: `Esconder conteúdos (${contentIds.length})`,
    disabled: contentIds.length === 0,
    onClick: () =>
      run(() => viewerApi.setItemsVisibility(modelId, contentIds, false)),
  })
  items.push({
    type: 'item',
    label: `Mostrar conteúdos (${contentIds.length})`,
    disabled: contentIds.length === 0,
    onClick: () =>
      run(() => viewerApi.setItemsVisibility(modelId, contentIds, true)),
  })

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={`Ações para ${titleLabel}`}
      className="fixed z-[900] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-1 text-xs shadow-2xl"
      style={{ left: x, top: y, width: W }}
    >
      <div className="border-b border-[var(--color-border)]/50 px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
        {titleLabel}
      </div>
      {items.map((it, i) =>
        it.type === 'sep' ? (
          <div
            key={`sep-${i}`}
            className="my-1 h-px bg-[var(--color-border)]/60"
          />
        ) : (
          <button
            key={`it-${i}-${it.label}`}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            onClick={it.onClick}
            className={`flex w-full items-center px-2 py-1 text-left transition-colors ${
              it.disabled
                ? 'cursor-not-allowed text-[var(--color-text-dim)] opacity-60'
                : 'text-[var(--color-text)] hover:bg-[var(--color-bg)]'
            }`}
          >
            {it.label}
          </button>
        ),
      )}
    </div>
  )
}

export default SpatialTree
